/**
 * RECORDS — the rows behind everything else on this screen.
 *
 * This is where every chart click lands, and it is the surface the four books'
 * report query feeds.
 *
 * What it adds over the screen it replaces:
 *
 *   · SORT, PAGING and COLUMN VISIBILITY, none of which existed;
 *   · a TRUNCATION BANNER, because the server announces a 5,000-row ceiling in
 *     `X-Report-Truncated` and nothing in the frontend has ever read it — so a
 *     truncated report rendered, and exported, as if it were complete;
 *   · CARD COLLAPSE under `sm`, keeping every field and the row action;
 *   · a row action that opens the REAL editor (`RecordDetail`, which PUTs and
 *     appends to `entry_field_edits` / `entry_versions`) rather than the inline
 *     cell editor that had no API call in it at all and discarded every edit.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMenuDismiss } from '../../../../hooks/use-menu-dismiss';
import styled from '@emotion/styled';
import { listItemMotion, m, overlayMotion, Presence } from '../../../../../motion';
import { FormModal } from '../../../../modal/form-modal';
import { RecordDetail } from '../../../../activity/record-detail';
import { PaginationButtons } from '../../dashboard-shared-components/table-pagination/table-pagination';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
} from '../../../../../style-utils/theme';
import { down, touch } from '../../../../../style-utils/media-queries';
import { PanelShell } from '../charts/chart-shell';
import { chipsFor } from '../state/filter-chips';
import {
  DataTable,
  PanelToggle,
  TableScroller,
  Td,
  Th,
} from '../charts/chart.styled';
import {
  Banner,
  FilterCaveats,
  Menu,
  MenuAnchor,
  MenuGroupLabel,
  MenuItem,
} from '../dashboard-screen.styled';
import { BOOK_LABELS, DashboardFilter } from '../state/filter-model';
import { FilterAction, hasAnyNarrowing } from '../state/filter-reducer';
import { RECORDS_PAGE_SIZE, Slice } from '../data/dashboard-data';
import { RecordsResponse, recordsBody } from '../data/records-api';
import {
  RECORD_COLUMNS,
  RecordColumn,
  cellText,
  columnsFor,
  defaultColumns,
  pillFor,
  recordRef,
} from './records-columns';
import { format } from '../charts/chart-theme';
import { formatWallText } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';
import { ClockNote } from '../../../../clock-note/clock-note';
import { bookNoun, openRecordLabel } from '../../../../../function-utils/record-label';

/* The columns menu grows from the button's corner and shrinks back into it. */
const POPOVER = overlayMotion('popover');
const POPOVER_ORIGIN = { transformOrigin: 'top right' } as const;

/* A ROW ARRIVING, capped cascade, NO layout animation: this is a 50-row page
   and `layout` on fifty rows is a measure per row per frame. Rows are keyed by
   record id, so a sort moves existing nodes without replaying the entrance and
   a page change (new ids) replays it. */
const rowMotion = (index: number) => ({ ...listItemMotion(index), layout: false });

/** A table row that can carry the row variants. */
const Row = m.tr``;

const DENSITIES: { value: 'comfortable' | 'compact'; label: string }[] = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

/* THE VALIDATED TRIPLES, NOT A LOCAL RE-INVENTION.
 *
 * This table used to pair each semantic hue with ITSELF on its own soft tint —
 * palette.warning on palette.warningSoft is 1.92:1, palette.alert on
 * palette.alertSoft is 2.1:1 — so the two states a reader most needs to spot
 * were the two hardest to read. theme.ts already carries { bg, fg, dot } triples
 * whose fg is chosen to pass on its own bg (4.56:1 to 9.20:1), which is exactly
 * what this needed and what design_system.md §2.3 says to use.
 *
 * The `dot` slot is deliberately unused here: these are text pills, and the dot
 * is the colour for the marker beside a label, not for the label. */
const PILL_TONE = {
  brand: { bg: status.brand.bg, fg: status.brand.fg },
  neutral: { bg: status.neutral.bg, fg: status.neutral.fg },
  success: { bg: status.success.bg, fg: status.success.fg },
  warning: { bg: status.warning.bg, fg: status.warning.fg },
  alert: { bg: status.alert.bg, fg: status.alert.fg },
} as const;

const Pill = styled.span<{ tone: keyof typeof PILL_TONE; glow?: boolean }>`
  display: inline-block;
  padding: 0 ${spacing.half};
  border-radius: ${radius.xs};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  color: ${({ tone }) => PILL_TONE[tone].fg};
  background: ${({ tone }) => PILL_TONE[tone].bg};
  box-shadow: ${({ glow, tone }) =>
    glow ? `0 0 0 2px ${PILL_TONE[tone].bg}, 0 0 10px ${PILL_TONE[tone].fg}55` : 'none'};
`;

const SortButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: ${({ $active }) => ($active ? palette.irisDeep : 'inherit')};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }

  /* THE ONE TOUCH TARGET THE CARD REFLOW DOES NOT COVER.
     Under sm this table becomes cards and these headers stop existing, so the
     sort control was never sized for a finger. But sm is a WIDTH, and the two
     touch devices that clear it — a phone held sideways at 750px, an iPad at
     768 — get the table, with a 17px header. Sized on touch rather than on a
     width for exactly that reason: a half-width desktop window is not a finger,
     and a 1024px iPad is. */
  ${touch} {
    min-height: ${layout.touchTarget};
    padding: ${spacing.half} 0;
  }
`;

/** UNDER `sm` THE TABLE BECOMES CARDS, one DOM, label:value rows. A wide table
 *  that only scrolls is legal but the brief asks for the reflow, and a row
 *  action a thumb cannot reach is a row action that does not exist. */
const Cards = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: none;
  flex-direction: column;
  gap: ${spacing.xs};

  ${down('sm')} {
    display: flex;
  }
`;

const Card = m.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.xs};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.xs};
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: ${spacing.half};
  font-size: ${typography.size.xs}px;

  > dt {
    margin: 0;
    color: ${palette.slateLight};
    font-size: ${typography.size.xxs}px;
    text-transform: uppercase;
    letter-spacing: ${typography.tracking.caps};
  }
  > dd {
    margin: 0;
    color: ${palette.ink};
    overflow-wrap: anywhere;
  }
`;

const DesktopOnly = styled.div`
  ${down('sm')} {
    display: none;
  }
`;

const OpenButton = styled.button`
  border: none;
  background: none;
  padding: ${spacing.half};
  font: inherit;
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  cursor: pointer;
  transition: ${transition.colors};
  /* One word, one line: at 1280 the table's auto layout squeezed the last
     column until "Open" broke into "Ope" over "n". NO BACKTICKS HERE. */
  white-space: nowrap;
  flex: 0 0 auto;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
    border-radius: ${radius.xs};
  }

  ${touch} {
    min-height: 44px;
    min-width: 44px;
  }
`;

/* A caveat that can also fix itself. Text-weight rather than a button: it sits
   inside a sentence, and a filled control there would read as the panel's
   primary action rather than as a correction to a warning. */
const InlineClear = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  cursor: pointer;
  text-decoration: underline;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
    border-radius: ${radius.xs};
  }
`;

export interface RecordsTabProps {
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  records: Slice<RecordsResponse>;
}

export const RecordsTab: React.FC<RecordsTabProps> = ({
  filter,
  dispatch,
  records,
}) => {
  /* THE CLOCK THESE ROWS ARE ON — the answer's own, from its `X-Report-*`
     headers (R-T12). Every row's `createdAt` is zone-less TEXT that is ALREADY
     on that clock and is printed as given; the other date columns are instants
     and are formatted in the same zone, so one row cannot be on two clocks. */
  const answered = records.data?.clock;
  const clock = useClock({
    buildingId: filter.buildingId,
    response: answered ? { tz: answered.tz, tz_source: answered.tzSource } : null,
  });
  const tz = clock.zone;
  const [columnMenu, setColumnMenu] = useState(false);
  /* This popover had NO dismissal at all — no Escape, no outside click. Once
     open it stayed open until one of its checkboxes was pressed, over the table
     it exists to configure. */
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const closeColumnMenu = useCallback(() => setColumnMenu(false), []);

  useMenuDismiss(columnMenu, closeColumnMenu, { menu: columnMenuRef });
  const [open, setOpen] = useState<{ source: string; id: string } | null>(null);

  const columns = columnsFor(filter.book, filter.columns, filter);
  const rows = records.data?.rows || [];

  /**
   * THE CHIPS THAT DO NOT NARROW THIS TABLE.
   *
   * `POST /dashboard/reports` accepts a subset of the filter. Kind, severity,
   * status, direction, group, who-recorded-it and the free-text search reach the
   * CHARTS and never this query — while the chip row above says they are on and
   * this panel's own caveat used to read "under exactly the filters that are
   * applied". A manager narrowing to Severity = Incident and switching here read
   * a table of every visit in the period and had no way to know.
   *
   * DERIVED, NOT LISTED. Asking the real `recordsBody` whether clearing a chip
   * changes the request is the only definition that cannot drift from the
   * request itself — a hand-maintained list of "fields the table ignores" is the
   * same two-lists-must-agree mistake that caused the bug in the first place.
   *
   * Each entry carries the chip's own patch, so the caveat is also the fix: the
   * reader can drop the filter that is misleading them without hunting for it.
   */
  const inertChips = useMemo(() => {
    const asked = JSON.stringify(recordsBody(filter, RECORDS_PAGE_SIZE));

    return chipsFor(filter).filter(chip => {
      const cleared = { ...filter, ...chip.patch };

      return JSON.stringify(recordsBody(cleared, RECORDS_PAGE_SIZE)) === asked;
    });
  }, [filter]);

  /**
   * SORTED IN THE BROWSER, over the page the server sent.
   *
   * `POST /dashboard/reports` orders by the log's own timestamp and takes no
   * sort parameter, so a client-side sort is the only one available — and it is
   * honest about its bounds: it sorts THIS PAGE, and the caption says so. A sort
   * that silently reordered one page of fifty while claiming to rank five
   * thousand would be worse than none.
   */
  const sorted = useMemo(() => {
    if (!filter.sort) return rows;
    const { column, dir } = filter.sort;
    const meta = RECORD_COLUMNS[filter.book].find(c => c.key === column);
    const factor = dir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      const av = a[column];
      const bv = b[column];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (meta?.numeric) return (Number(av) - Number(bv)) * factor;

      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [rows, filter.sort, filter.book]);

  const cell = (column: RecordColumn, row: Record<string, unknown>) => {
    const text = cellText(column, row[column.key]);
    if (!text) return '—';
    if (column.kind === 'date' && column.key !== 'createdAt') {
      return format.instant(String(row[column.key]), tz);
    }
    const pill = pillFor(column.key, text);
    if (pill) {
      return (
        <Pill tone={pill.tone} glow={pill.glow}>
          {text}
        </Pill>
      );
    }

    return text;
  };

  /* WP-36 / MR-14 — THE ROW, IN THE BUTTON'S NAME.
   *
   * Every Open button on this table had the accessible name "Open" and nothing
   * else, so the standard way to move through a page — pulling up the list of
   * controls — produced forty identical entries. The visible word stays "Open";
   * the column is narrow and the row is right there for anyone who can see it.
   *
   * READ FROM THE ROW, NOT FROM THE COLUMN SELECTION. A reader who has hidden
   * the Unit column has changed what THEY see; it must not change what the
   * button is called for somebody who cannot see the table at all.
   *
   * The noun comes from the BOOK rather than from `recordRef`'s source, because
   * that maps both Visitor Log and Resident Log onto `visitor_log` — deriving it
   * from the source would call a resident record a visitor entry.
   */
  const openLabel = (row: Record<string, unknown>): string => {
    const text = (key: string): string => {
      const raw = row[key];
      if (raw == null) return '';
      if (Array.isArray(raw)) return raw.filter(Boolean).join(' ');
      if (typeof raw === 'object') return '';

      return String(raw).trim();
    };

    // The four books spell a person's name with four different pairs of keys.
    const who = [
      text('names') || text('name'),
      text('lastNames') || text('lastName'),
    ]
      .filter(Boolean)
      .join(' ');
    const unit = text('apartmentLocation');
    // `createdAt` is the server's wall-clock text, not an instant: reading it
    // through a Date re-reads it on THIS device's clock, and is Invalid Date in
    // WebKit. Printed as given.
    const when = row.createdAt ? formatWallText(String(row.createdAt)) : '';

    return openRecordLabel(bookNoun(filter.book), [
      who,
      unit ? `unit ${unit}` : '',
      when,
    ]);
  };

  const toggleColumn = (key: string) => {
    const current = filter.columns && filter.columns.length
      ? filter.columns
      : defaultColumns(filter.book);
    const next = current.includes(key)
      ? current.filter(c => c !== key)
      : [...current, key];
    dispatch({ type: 'setColumns', columns: next.length ? next : null });
  };

  const page = filter.page;
  const hasNext = rows.length === RECORDS_PAGE_SIZE;

  return (
    <>
      <PanelShell
        id="records"
        title={`${BOOK_LABELS[filter.book]} — records`}
        wide
        state={{
          loading: records.loading,
          stale: records.stale,
          error: records.error,
          onRetry: records.retry,
        }}
        caveat={
          inertChips.length
            ? 'Every row this book holds for the filters this table can honour. Sorting reorders this page; it does not re-rank the whole result.'
            : 'Every row behind the figures above, under exactly the filters that are applied. Sorting reorders this page; it does not re-rank the whole result.'
        }
        extraCaveat={
          inertChips.length ? (
            <FilterCaveats>
              {inertChips.map(chip => (
                <li key={chip.id}>
                  {chip.label}: {chip.value} narrows the charts above, not this
                  table.{' '}
                  <InlineClear
                    type="button"
                    onClick={() =>
                      dispatch({ type: 'clearChip', patch: chip.patch })
                    }
                  >
                    Clear it
                  </InlineClear>
                </li>
              ))}
            </FilterCaveats>
          ) : null
        }
        emptyReason={
          records.data && !rows.length
            ? {
                title: 'No records match',
                text: 'Nothing in this book matches the filters that are applied.',
                /* THE EMPTINESS IS THE READER'S OWN FILTER, so the undo belongs
                   here rather than as an instruction to go and find the chips.
                   Only offered when something is actually narrowing — on an
                   unfiltered book the list is empty because the book is. */
                action: hasAnyNarrowing(filter)
                  ? {
                      label: 'Clear all filters',
                      onPress: () => dispatch({ type: 'clearAll' }),
                    }
                  : undefined,
              }
            : null
        }
        controls={
          <MenuAnchor>
            <PanelToggle
              type="button"
              aria-haspopup="menu"
              aria-expanded={columnMenu}
              data-testid="column-menu"
              onClick={() => setColumnMenu(v => !v)}
            >
              Columns
            </PanelToggle>
            <Presence>
            {columnMenu ? (
              <Menu
                key="column-menu"
                ref={columnMenuRef}
                {...POPOVER}
                style={POPOVER_ORIGIN}
                role="menu"
                aria-label="Table appearance"
              >
                <MenuGroupLabel id="records-column-group">Columns</MenuGroupLabel>
                <div role="group" aria-labelledby="records-column-group">
                {RECORD_COLUMNS[filter.book].map(column => {
                  const shown = columns.some(c => c.key === column.key);

                  return (
                    <MenuItem
                      key={column.key}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={shown}
                      data-testid={`column-${column.key}`}
                      onClick={() => toggleColumn(column.key)}
                    >
                      {column.label}
                      <span aria-hidden="true">{shown ? '✓' : ''}</span>
                    </MenuItem>
                  );
                })}
                </div>

                {/* ROW HEIGHT - the control this setting never had.
                    `density` was already in the filter model, already validated,
                    already serialised to the URL as `dn` and already saved to and
                    restored from the device prefs. All of that machinery existed
                    to carry a preference that nothing on screen could set and
                    nothing on screen could show. This is the missing end of it. */}
                <MenuGroupLabel id="records-density-group">Row height</MenuGroupLabel>
                <div role="group" aria-labelledby="records-density-group">
                  {DENSITIES.map(option => (
                    <MenuItem
                      key={option.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={filter.density === option.value}
                      data-testid={`density-${option.value}`}
                      onClick={() => dispatch({ type: 'setDensity', density: option.value })}
                    >
                      {option.label}
                      <span aria-hidden="true">
                        {filter.density === option.value ? '✓' : ''}
                      </span>
                    </MenuItem>
                  ))}
                </div>
              </Menu>
            ) : null}
            </Presence>
          </MenuAnchor>
        }
      >
        {/* THE TRUNCATION BANNER. The server sets X-Report-Truncated and exposes
            it deliberately; nothing in the frontend had ever read it, so a
            truncated report rendered — and exported — as if it were complete. */}
        {records.data?.truncated ? (
          <Banner role="status" data-testid="truncation-banner">
            Showing the first {(records.data.rowLimit || 0).toLocaleString()} rows the
            server would return. Narrow the period or the building to see the rest —
            every count on this screen derived from these rows is a floor.
          </Banner>
        ) : null}

        {/* WHICH CLOCK EVERY TIME IN THIS TABLE IS ON, with the server's own
            label. One note for the table — the building's own time for a row is
            in that record's detail. */}
        {rows.length ? <ClockNote clock={clock} label={answered?.label} /> : null}

        <DesktopOnly>
          <TableScroller>
            <DataTable data-testid="records-table" $dense={filter.density === 'compact'} $wrapHeads>
              <thead>
                <tr>
                  {columns.map(column => (
                    <Th
                      key={column.key}
                      scope="col"
                      numeric={column.numeric}
                      /* aria-sort ON THE HEADER CELL is how a screen reader
                         announces which column a table is sorted by and in which
                         direction. The only indication was a triangle character
                         inside the button label, which is read out as a glyph
                         name if at all — so the sort state was visible and
                         unspoken. */
                      aria-sort={
                        filter.sort?.column === column.key
                          ? filter.sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      {column.sortable ? (
                        <SortButton
                          type="button"
                          $active={filter.sort?.column === column.key}
                          /* The label says what pressing it DOES, and names the
                             direction it will move to — "Sort by Unit" on a
                             column already sorted ascending told the reader
                             nothing about what would happen. */
                          aria-label={
                            filter.sort?.column === column.key
                              ? `${column.label}, sorted ${
                                  filter.sort.dir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                }. Activate to reverse.`
                              : `Sort by ${column.label}`
                          }
                          data-testid={`sort-${column.key}`}
                          onClick={() => dispatch({ type: 'setSort', column: column.key })}
                        >
                          {column.label}
                          <span aria-hidden="true">
                            {filter.sort?.column === column.key
                              ? filter.sort.dir === 'asc'
                                ? ' ▲'
                                : ' ▼'
                              : ''}
                          </span>
                        </SortButton>
                      ) : (
                        column.label
                      )}
                    </Th>
                  ))}
                  <Th scope="col" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => {
                  const ref = recordRef(filter.book, row);

                  return (
                    <Row
                      key={ref?.id || `row-${i}`}
                      {...rowMotion(i)}
                      /* Audit hooks: the instant, the text the server sent for
                         it (already on the answer's clock), and the row's own
                         building's zone. */
                      data-at={row.utcCreatedAt ? String(row.utcCreatedAt) : undefined}
                      data-created={row.createdAt ? String(row.createdAt) : undefined}
                      data-building-zone={row.tz ? String(row.tz) : undefined}
                    >
                      {columns.map(column => (
                        <Td key={column.key} numeric={column.numeric}>
                          {cell(column, row)}
                        </Td>
                      ))}
                      <Td>
                        {ref ? (
                          <OpenButton
                            type="button"
                            data-testid={`open-${ref.id}`}
                            aria-label={openLabel(row)}
                            onClick={() => setOpen(ref)}
                          >
                            Open
                          </OpenButton>
                        ) : null}
                      </Td>
                    </Row>
                  );
                })}
              </tbody>
            </DataTable>
          </TableScroller>
        </DesktopOnly>

        <Cards data-testid="records-cards">
          {sorted.map((row, i) => {
            const ref = recordRef(filter.book, row);

            return (
              <Card
                key={ref?.id || `card-${i}`}
                {...rowMotion(i)}
                /* The same audit hooks as the table's rows: a phone shows these
                   cards and not the table. */
                data-at={row.utcCreatedAt ? String(row.utcCreatedAt) : undefined}
                data-created={row.createdAt ? String(row.createdAt) : undefined}
                data-building-zone={row.tz ? String(row.tz) : undefined}
              >
                <dl style={{ margin: 0 }}>
                  {columns.map(column => (
                    <CardRow key={column.key}>
                      <dt>{column.label}</dt>
                      <dd>{cell(column, row)}</dd>
                    </CardRow>
                  ))}
                </dl>
                {ref ? (
                  <OpenButton
                    type="button"
                    aria-label={openLabel(row)}
                    onClick={() => setOpen(ref)}
                  >
                    Open
                  </OpenButton>
                ) : null}
              </Card>
            );
          })}
        </Cards>

        <PaginationButtons
          currentPage={page + 1}
          hasPreviousPage={page > 0}
          hasNextPage={hasNext}
          previousPage={() => dispatch({ type: 'setPage', page: Math.max(0, page - 1) })}
          nextPage={() => dispatch({ type: 'setPage', page: page + 1 })}
          totalPages={null}
        />
      </PanelShell>

      {/* THE REAL EDITOR. It PUTs through the existing log endpoints, which are
          permission-gated and already append to entry_field_edits and
          entry_versions — so a correction made here shows up in that record's own
          history AND in the org-wide corrections log without any extra work. */}
      {open ? (
        <FormModal wide title="Record" onClose={() => setOpen(null)}>
          <RecordDetail
            source={open.source}
            id={open.id}
            onClose={() => setOpen(null)}
            // An out taken from the record is a new row in this table.
            onChanged={records.retry}
          />
        </FormModal>
      ) : null}
    </>
  );
};
